import React, { useState, useRef, useEffect, useCallback } from "react";
import { FileText, Mail, Reply, ScanLine, BrainCircuit, Loader2, Copy, Download, Check, Upload, Wand2, ArrowRight, Paperclip, X, Printer, FileSignature, Archive, Files, Save, Camera, KeyRound, ShieldCheck, Brain, AtSign } from "lucide-react";
import { helperGenerate, helperScan, helperSignedFolders, helperSaveTemplate } from "../lib/api";
import DocumentSigner from "./DocumentSigner";

// The model writes the formal title as line 1; use it as the PDF heading and keep it out of the body.
const splitDoc = (text) => {
  const lines = String(text || "").split("\n");
  const i = lines.findIndex((l) => l.trim());
  if (i < 0) return { title: "Document", body: "" };
  const t = lines[i].replace(/^[#*\s]+|[*\s]+$/g, "").trim();
  if (t.length > 90) return { title: "Document", body: text };
  return { title: t, body: lines.slice(i + 1).join("\n").replace(/^\s+/, "") };
};
import SignedDocs from "./SignedDocs";
import Templates from "./Templates";
import SnapDoc from "./SnapDoc";
import DeveloperApi from "./DeveloperApi";
import MasterVault from "./MasterVault";
import NexusPrivate from "./NexusPrivate";
import NexusMail from "./NexusMail";
import { toast } from "sonner";

const TOOLS = [
  { id: "private", label: "Nexus (Private)", icon: Brain, hint: "Talk to Nexus — honest advice; he learns about you", ph: "" },
  { id: "mail", label: "Nexus Mail", icon: AtSign, hint: "Nexus's own email — send & receive on your behalf", ph: "" },
  { id: "vault", label: "My Vault", icon: ShieldCheck, hint: "Your personal info — auto-fills every doc", ph: "" },
  { id: "document", label: "Document", icon: FileText, hint: "Draft a letter, notice, memo, proposal…", ph: "e.g. A formal rent-increase notice to my tenant, 30 days, 8% increase." },
  { id: "email", label: "Email", icon: Mail, hint: "Write a complete email with subject + body", ph: "e.g. Email to my accountant asking for last quarter's P&L by Friday." },
  { id: "reply", label: "Reply", icon: Reply, hint: "Paste a message/email → get the perfect reply", ph: "How should I respond? (tone/points to make)" },
  { id: "snapdoc", label: "SnapDoc", icon: Camera, hint: "Snap paper → clean PDF → print/share/email", ph: "" },
  { id: "scan", label: "Scan Paper", icon: ScanLine, hint: "Photo or PDF → extract the text (OCR)", ph: "" },
  { id: "logic", label: "Run Logic", icon: BrainCircuit, hint: "Analyse, calculate, plan, decide", ph: "e.g. Should I lease or buy a $40k van used 20k miles/yr? Show the math." },
  { id: "templates", label: "Templates", icon: Files, hint: "Your saved letters — load one to reuse", ph: "" },
  { id: "saved", label: "Signed Docs", icon: Archive, hint: "Your saved & signed PDFs — search and re-share", ph: "" },
  { id: "api", label: "Developer API", icon: KeyRound, hint: "API keys to embed SnapDoc & DigiSign in your apps", ph: "" },
];
const TONES = ["Professional", "Friendly", "Formal", "Firm", "Concise", "Persuasive", "Warm"];

export default function AVHelper({ session }) {
  const userId = session.user_id;
  const [tool, setTool] = useState("document");
  const [instructions, setInstructions] = useState("");
  const [source, setSource] = useState("");
  const [tone, setTone] = useState("Professional");
  const [output, setOutput] = useState("");
  const [busy, setBusy] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [copied, setCopied] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [attaching, setAttaching] = useState(false);
  const [showSigner, setShowSigner] = useState(false);
  const [signedRefresh, setSignedRefresh] = useState(0);
  const [folders, setFolders] = useState([]);
  const [savingTpl, setSavingTpl] = useState(false);
  const fileRef = useRef(null);
  const attachRef = useRef(null);

  const active = TOOLS.find((t) => t.id === tool);

  const loadFolders = useCallback(async () => {
    try { const r = await helperSignedFolders(userId); setFolders(r.folders || []); } catch (e) {}
  }, [userId]);
  useEffect(() => { loadFolders(); }, [loadFolders, signedRefresh]);

  const saveAsTemplate = async () => {
    const name = window.prompt("Name this template (e.g. 'Sponsor letter'):");
    if (!name || !name.trim()) return;
    setSavingTpl(true);
    try { await helperSaveTemplate(userId, { name: name.trim(), body: output, task: tool, tone }); toast.success("Saved to Templates"); }
    catch (e) { toast.error("Could not save the template"); }
    finally { setSavingTpl(false); }
  };

  const loadTemplate = (tpl) => {
    setTool(tpl.task && TOOLS.find((t) => t.id === tpl.task) ? tpl.task : "document");
    setOutput(tpl.body || "");
    if (tpl.tone) setTone(tpl.tone);
    toast.message("Loaded — edit names/dates, then Sign & PDF");
  };

  const run = async () => {
    if (tool === "scan") { fileRef.current?.click(); return; }
    const parts = [];
    if (source.trim()) parts.push(source.trim());
    for (const a of attachments) parts.push(`[Attached file: ${a.name}]\n${a.text}`);
    const source_text = parts.join("\n\n");
    if (!instructions.trim() && !source_text.trim()) { toast.message("Tell AV Helper what you need (or attach a file)."); return; }
    setBusy(true); setOutput("");
    try {
      const r = await helperGenerate(userId, { task: tool, instructions, tone, source_text });
      setOutput(r.text || "");
    } catch (e) { toast.error("AV Helper could not finish that"); }
    finally { setBusy(false); }
  };

  const onAttach = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setAttaching(true);
    try {
      for (const f of files) {
        const isImage = (f.type || "").startsWith("image/");
        const isPdf = (f.type || "") === "application/pdf" || /\.pdf$/i.test(f.name || "");
        let dataUrl = null;
        if (isImage) dataUrl = await new Promise((res) => { const r = new FileReader(); r.onload = () => res(r.result); r.readAsDataURL(f); });
        const r = await helperScan(userId, [f]);
        setAttachments((prev) => [...prev, { name: f.name, text: r.text || "", dataUrl, isImage, isPdf, file: isPdf ? f : null }]);
      }
      toast.success("Attached — AV Helper will read it");
    } catch (err) { toast.error("Could not read that attachment"); }
    finally { setAttaching(false); if (attachRef.current) attachRef.current.value = ""; }
  };

  const onScan = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setScanning(true); setOutput("");
    try {
      const r = await helperScan(userId, files);
      const text = r.text || "";
      setSource(text);
      setOutput(text);
      toast.success("Text extracted — send it to a Reply or Document below");
    } catch (err) { toast.error("Could not read that document"); }
    finally { setScanning(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const copy = () => { navigator.clipboard.writeText(output).then(() => { setCopied(true); setTimeout(() => setCopied(false), 1500); }); };
  const download = () => {
    const blob = new Blob([output], { type: "text/plain" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob);
    a.download = `av-helper-${tool}.txt`; a.click(); URL.revokeObjectURL(a.href);
  };
  const escapeHtml = (s) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  const printOutput = () => {
    const w = window.open("", "_blank");
    if (!w) { toast.error("Allow pop-ups to print"); return; }
    w.document.write(`<html><head><title>AV Helper — ${tool}</title><style>body{font-family:Georgia,'Times New Roman',serif;white-space:pre-wrap;padding:48px 56px;line-height:1.7;font-size:14px;color:#111}</style></head><body>${escapeHtml(output)}</body></html>`);
    w.document.close(); w.focus(); setTimeout(() => { w.print(); }, 250);
  };
  const emailOutput = () => {
    let subject = tool === "document" ? "Document" : "Message from AV Helper";
    let body = output;
    const m = output.match(/^\s*Subject:\s*(.+)$/im);
    if (m) { subject = m[1].trim(); body = output.replace(/^\s*Subject:\s*.+\r?\n?/i, "").trim(); }
    window.location.href = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };
  const sendScanTo = (dest) => { setTool(dest); toast.message(`Loaded into ${dest}. Add your instructions and Generate.`); };

  const inp = "w-full bg-[#0a1526] text-cyan-50 border border-[#00F0FF33] rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-[#00F0FF]";

  return (
    <div className="min-h-[calc(100vh-56px)] bg-[#030712] nx-display" data-testid="av-helper">
      <input ref={fileRef} type="file" accept="image/*,.pdf,.docx" multiple className="hidden" onChange={onScan} data-testid="av-helper-file" />
      <input ref={attachRef} type="file" accept="image/*,.pdf,.docx" multiple className="hidden" onChange={onAttach} data-testid="av-attach-file" />

      {/* header */}
      <div className="px-5 py-4 border-b border-[#00F0FF22] flex items-center gap-3">
        <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: "radial-gradient(circle at 40% 30%, #00F0FF, #061018 75%)", boxShadow: "0 0 20px #00F0FF66" }}>
          <Wand2 className="w-5 h-5 text-white" />
        </div>
        <div>
          <h1 className="nx-orbitron font-black tracking-[0.25em] text-[#00F0FF] text-lg leading-none">AV HELPER</h1>
          <p className="text-cyan-100/45 text-[11px] nx-mono mt-0.5">Your private desk — drafts, emails, replies, scans & logic · Master only</p>
        </div>
      </div>

      {/* tool selector */}
      <div className="px-5 pt-4 flex flex-wrap gap-2" data-testid="av-helper-tools">
        {TOOLS.map((t) => (
          <button key={t.id} onClick={() => { setTool(t.id); setOutput(""); }} data-testid={`av-tool-${t.id}`}
            className={`px-3.5 py-2 rounded-xl nx-mono text-xs font-bold flex items-center gap-2 border transition-colors ${tool === t.id ? "text-[#030712] bg-[#00F0FF] border-[#00F0FF]" : "text-cyan-200/70 border-cyan-200/20 hover:text-cyan-100 hover:border-cyan-200/40"}`}>
            <t.icon className="w-4 h-4" /> {t.label}
          </button>
        ))}
      </div>

      <div className="p-5 grid grid-cols-1 lg:grid-cols-2 gap-5">
        {tool === "saved" ? (
          <SignedDocs userId={userId} refreshKey={signedRefresh} onChanged={() => setSignedRefresh((n) => n + 1)} />
        ) : tool === "templates" ? (
          <Templates userId={userId} onLoad={loadTemplate} />
        ) : tool === "snapdoc" ? (
          <SnapDoc userId={userId} />
        ) : tool === "api" ? (
          <DeveloperApi userId={userId} />
        ) : tool === "vault" ? (
          <MasterVault userId={userId} />
        ) : tool === "private" ? (
          <NexusPrivate userId={userId} />
        ) : tool === "mail" ? (
          <NexusMail userId={userId} />
        ) : (
        <>
        {/* input panel */}
        <div className="nx-glass rounded-2xl p-4 space-y-3" data-testid="av-input-panel">
          <p className="text-cyan-200/60 text-xs nx-mono tracking-widest flex items-center gap-2"><active.icon className="w-4 h-4" /> {active.hint}</p>

          {tool === "scan" ? (
            <div className="space-y-3">
              <button onClick={() => fileRef.current?.click()} disabled={scanning} data-testid="av-scan-btn"
                className="w-full py-10 rounded-2xl border-2 border-dashed border-[#00F0FF44] flex flex-col items-center gap-2 text-cyan-200/70 hover:bg-[#00F0FF08]">
                {scanning ? <Loader2 className="w-8 h-8 animate-spin text-[#00F0FF]" /> : <Upload className="w-8 h-8 text-[#00F0FF]" />}
                <span className="nx-mono text-sm">{scanning ? "Reading the document…" : "Tap to upload a photo or PDF"}</span>
                <span className="text-[11px] text-cyan-100/40">JPG · PNG · PDF · DOCX</span>
              </button>
              {source && (
                <div className="flex gap-2">
                  <button onClick={() => sendScanTo("reply")} data-testid="av-scan-to-reply" className="flex-1 py-2 rounded-xl nx-mono text-xs text-[#00F0FF] border border-[#00F0FF44] hover:bg-[#00F0FF11] flex items-center justify-center gap-1">Reply to this <ArrowRight className="w-3 h-3" /></button>
                  <button onClick={() => sendScanTo("document")} data-testid="av-scan-to-doc" className="flex-1 py-2 rounded-xl nx-mono text-xs text-[#00F0FF] border border-[#00F0FF44] hover:bg-[#00F0FF11] flex items-center justify-center gap-1">Draft from this <ArrowRight className="w-3 h-3" /></button>
                </div>
              )}
            </div>
          ) : (
            <>
              {tool === "reply" && (
                <div>
                  <label className="text-cyan-200/50 text-[11px] nx-mono">PASTE THE MESSAGE / EMAIL TO REPLY TO</label>
                  <textarea className={inp} rows={5} value={source} onChange={(e) => setSource(e.target.value)} placeholder="Paste what they sent you here…" data-testid="av-source" />
                </div>
              )}
              <div>
                <label className="text-cyan-200/50 text-[11px] nx-mono">{tool === "reply" ? "HOW SHOULD I REPLY? (optional)" : "WHAT DO YOU NEED?"}</label>
                <textarea className={inp} rows={tool === "reply" ? 3 : 6} value={instructions} onChange={(e) => setInstructions(e.target.value)} placeholder={active.ph} data-testid="av-instructions" />
              </div>
              <div className="flex items-center gap-2">
                <label className="text-cyan-200/50 text-[11px] nx-mono">TONE</label>
                <select value={tone} onChange={(e) => setTone(e.target.value)} data-testid="av-tone" className="bg-[#0a1526] text-cyan-50 border border-[#00F0FF33] rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:border-[#00F0FF]">
                  {TONES.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              {/* attachments — Nexus reads these */}
              <div className="space-y-2">
                <div className="flex items-center gap-2 flex-wrap">
                  <button onClick={() => attachRef.current?.click()} disabled={attaching} data-testid="av-attach-btn"
                    className="px-3 py-1.5 rounded-lg nx-mono text-[11px] text-[#00F0FF] border border-[#00F0FF44] hover:bg-[#00F0FF11] flex items-center gap-1.5">
                    {attaching ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Paperclip className="w-3.5 h-3.5" />} Attach files
                  </button>
                  <span className="text-cyan-100/35 text-[10px] nx-mono">photos · PDF · Word — Nexus reads them</span>
                </div>
                {attachments.length > 0 && (
                  <div className="flex flex-wrap gap-1.5" data-testid="av-attachments">
                    {attachments.map((a, i) => (
                      <span key={i} className="px-2 py-1 rounded-md bg-[#0a1526] border border-[#00F0FF33] text-cyan-100/80 text-[11px] flex items-center gap-1" data-testid="av-attachment-chip">
                        <FileText className="w-3 h-3" /> {a.name.length > 24 ? a.name.slice(0, 22) + "…" : a.name}
                        <button onClick={() => setAttachments((prev) => prev.filter((_, j) => j !== i))} className="text-cyan-100/40 hover:text-red-400 ml-0.5"><X className="w-3 h-3" /></button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
              <button onClick={run} disabled={busy} data-testid="av-generate"
                className="w-full py-3 rounded-xl nx-orbitron font-bold tracking-wide text-[#030712] bg-[#00F0FF] disabled:opacity-40 flex items-center justify-center gap-2">
                {busy ? <><Loader2 className="w-5 h-5 animate-spin" /> Working…</> : <><Wand2 className="w-5 h-5" /> Generate</>}
              </button>
            </>
          )}
        </div>

        {/* output panel */}
        <div className="nx-glass rounded-2xl p-4 flex flex-col" data-testid="av-output-panel" style={{ minHeight: 320 }}>
          <div className="flex items-center justify-between mb-2">
            <p className="text-cyan-200/60 text-xs nx-mono tracking-widest">RESULT</p>
            {output && (
              <div className="flex gap-2 flex-wrap">
                <button onClick={copy} data-testid="av-copy" className="px-2.5 py-1 rounded-lg nx-mono text-[11px] text-[#00F0FF] border border-[#00F0FF44] hover:bg-[#00F0FF11] flex items-center gap-1">{copied ? <><Check className="w-3 h-3" /> Copied</> : <><Copy className="w-3 h-3" /> Copy</>}</button>
                <button onClick={printOutput} data-testid="av-print" className="px-2.5 py-1 rounded-lg nx-mono text-[11px] text-[#00F0FF] border border-[#00F0FF44] hover:bg-[#00F0FF11] flex items-center gap-1"><Printer className="w-3 h-3" /> Print</button>
                <button onClick={emailOutput} data-testid="av-email" className="px-2.5 py-1 rounded-lg nx-mono text-[11px] text-[#00F0FF] border border-[#00F0FF44] hover:bg-[#00F0FF11] flex items-center gap-1"><Mail className="w-3 h-3" /> Email</button>
                <button onClick={download} data-testid="av-download" className="px-2.5 py-1 rounded-lg nx-mono text-[11px] text-[#00F0FF] border border-[#00F0FF44] hover:bg-[#00F0FF11] flex items-center gap-1"><Download className="w-3 h-3" /> Save</button>
                <button onClick={saveAsTemplate} disabled={savingTpl} data-testid="av-save-template" className="px-2.5 py-1 rounded-lg nx-mono text-[11px] text-[#00F0FF] border border-[#00F0FF44] hover:bg-[#00F0FF11] flex items-center gap-1 disabled:opacity-40">{savingTpl ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />} Template</button>
                <button onClick={() => setShowSigner(true)} data-testid="av-sign-pdf" className="px-2.5 py-1 rounded-lg nx-mono text-[11px] text-[#030712] bg-[#00F0FF] hover:opacity-90 flex items-center gap-1"><FileSignature className="w-3 h-3" /> DigiSign</button>
              </div>
            )}
          </div>
          <div className="flex-1 overflow-y-auto">
            {(busy || scanning) && !output && <div className="h-full flex items-center justify-center"><Loader2 className="w-7 h-7 text-cyan-400 animate-spin" /></div>}
            {output ? (
              <pre className="text-cyan-50 text-sm leading-relaxed whitespace-pre-wrap font-sans" data-testid="av-output-text">{output}</pre>
            ) : (!busy && !scanning && (
              <div className="h-full flex flex-col items-center justify-center text-center text-cyan-100/30 gap-2">
                <Wand2 className="w-8 h-8" />
                <p className="text-sm nx-mono">Your finished {tool === "scan" ? "extracted text" : tool} will appear here</p>
              </div>
            ))}
          </div>
        </div>
        </>
        )}
      </div>
      {showSigner && (
        <DocumentSigner
          title={splitDoc(output).title}
          body={splitDoc(output).body} attachments={attachments}
          pdfAttachments={attachments.filter((a) => a.isPdf && a.file)}
          userId={userId} folders={folders}
          onSaved={() => setSignedRefresh((n) => n + 1)}
          onClose={() => setShowSigner(false)} />
      )}
    </div>
  );
}
